import React from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { alpha } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import { getBestAvailableCurrentPosition } from "../../utils/geo";
import { markFirstRunOnboardingComplete } from "./firstRunOnboardingStorage";
import { onboardingSlides } from "./onboardingSlides";

const MAP_CENTER_KEY = "mapCenter";

const floatPin = keyframes`
  0%, 100% { transform: translate(-50%, -50%) translateY(0) scale(1); }
  50% { transform: translate(-50%, -50%) translateY(-8px) scale(1.04); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 0.95; transform: scale(1.08); }
`;

function saveMapCenter(position) {
  const lat = Number(position?.lat);
  const lng = Number(position?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

  try {
    window.localStorage.setItem(MAP_CENTER_KEY, JSON.stringify({ lat, lng }));
  } catch {
    // Home already has fallbacks for storage-limited sessions.
  }
}

function ExampleTicker({ examples, accent, label = "Try asking" }) {
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    if (!examples?.length) return undefined;
    const timerId = window.setInterval(() => {
      setIndex((current) => (current + 1) % examples.length);
    }, 1800);
    return () => window.clearInterval(timerId);
  }, [examples]);

  return (
    <Box
      sx={{
        flex: "0 0 auto",
        maxHeight: { xs: 96, sm: 108 },
        overflow: "hidden",
        px: { xs: 1.4, sm: 1.6 },
        py: { xs: 0.8, sm: 0.9 },
        "@media (max-height: 740px)": {
          px: 1.2,
          py: 0.55,
          maxHeight: 82,
        },
        "@media (max-height: 620px)": {
          px: 1,
          py: 0.4,
          maxHeight: 68,
        },
      }}
    >
      <Typography
        sx={{
          color: accent,
          fontSize: 11,
          fontWeight: 900,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        key={examples[index]}
        sx={{
          mt: 0.7,
          minHeight: { xs: 48, sm: 54 },
          color: "text.primary",
          fontSize: { xs: "clamp(18px, 5.6vw, 22px)", sm: 24 },
          fontWeight: 900,
          lineHeight: 1.12,
          "@media (max-height: 740px)": {
            minHeight: 38,
            fontSize: "clamp(16px, 5vw, 19px)",
          },
          "@media (max-height: 620px)": {
            minHeight: 28,
            fontSize: "clamp(15px, 4.8vw, 17px)",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
          },
        }}
      >
        "{examples[index]}"
      </Typography>
    </Box>
  );
}

function MapPreview({ slide }) {
  const mapMarkers = slide.mapMarkers || [];

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "relative",
        height: { xs: "clamp(190px, 32dvh, 285px)", sm: "clamp(260px, 38dvh, 340px)" },
        minHeight: 0,
        borderRadius: 4,
        overflow: "hidden",
        background:
          "linear-gradient(135deg, rgba(86,195,114,0.28), rgba(17,82,228,0.16)), #D7EEE5",
        border: "1px solid rgba(255,255,255,0.24)",
        boxShadow: "0 24px 70px rgba(0,0,0,0.28)",
        "@media (max-height: 740px)": {
          height: { xs: "clamp(150px, 27dvh, 205px)", sm: "clamp(220px, 34dvh, 300px)" },
        },
        "@media (max-height: 620px)": {
          height: { xs: "clamp(124px, 24dvh, 168px)", sm: "clamp(190px, 30dvh, 250px)" },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(32deg, transparent 0 31%, rgba(255,255,255,0.38) 32% 34%, transparent 35%), linear-gradient(132deg, transparent 0 58%, rgba(255,255,255,0.32) 59% 61%, transparent 62%)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: "12%",
          top: "22%",
          width: 86,
          height: 86,
          borderRadius: "50%",
          backgroundColor: alpha(slide.accent, 0.18),
          animation: `${pulse} 3s ease-in-out infinite`,
        }}
      />
      {mapMarkers.map((marker) => (
        <Box
          key={`${marker.src}-${marker.left}-${marker.top}`}
          sx={{
            position: "absolute",
            left: marker.left,
            top: marker.top,
            width: { xs: 50, sm: 60 },
            height: { xs: 50, sm: 60 },
            transform: "translate(-50%, -50%)",
            animation: `${floatPin} 3.2s ease-in-out infinite`,
            animationDelay: marker.delay,
            filter: "drop-shadow(0 14px 14px rgba(0,0,0,0.24))",
            "@media (max-height: 620px)": {
              width: { xs: 40, sm: 50 },
              height: { xs: 40, sm: 50 },
            },
          }}
        >
          <Box
            component="img"
            data-testid="onboarding-map-marker"
            src={marker.src}
            alt=""
            draggable={false}
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </Box>
      ))}
      <Box
        sx={{
          position: "absolute",
          left: 18,
          right: 18,
          bottom: 18,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          borderRadius: 3,
          p: 1.4,
          backgroundColor: "rgba(255,255,255,0.92)",
          color: "#17201D",
          boxShadow: "0 18px 40px rgba(24,36,31,0.2)",
        }}
      >
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            backgroundColor: alpha(slide.accent, 0.14),
            flex: "0 0 auto",
            "@media (max-height: 620px)": {
              width: 38,
              height: 38,
            },
          }}
        >
          <Box
            component="img"
            data-testid="onboarding-card-icon"
            src={slide.cardIcon}
            alt=""
            draggable={false}
            sx={{
              display: "block",
              width: "82%",
              height: "82%",
              objectFit: "contain",
            }}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
            Living local map
          </Typography>
          <Typography sx={{ fontSize: 12, color: "rgba(23,32,29,0.68)" }}>
            {slide.mapCaption}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function FirstRunOnboardingPage({ onComplete }) {
  const [slideIndex, setSlideIndex] = React.useState(0);
  const [isLocating, setIsLocating] = React.useState(false);
  const touchStartRef = React.useRef(null);
  const slide = onboardingSlides[slideIndex];
  const isFinalSlide = slideIndex === onboardingSlides.length - 1;

  const complete = React.useCallback(
    (details) => {
      markFirstRunOnboardingComplete();
      onComplete?.(details);
    },
    [onComplete]
  );

  const handleSkip = React.useCallback(() => {
    complete({ requestedLocation: false });
  }, [complete]);

  const handlePrimary = React.useCallback(async () => {
    if (!isFinalSlide) {
      setSlideIndex((current) => Math.min(current + 1, onboardingSlides.length - 1));
      return;
    }

    if (isLocating) return;
    setIsLocating(true);

    try {
      const position = await getBestAvailableCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
      });
      saveMapCenter(position);
      complete({ requestedLocation: true, locationGranted: true });
    } catch {
      complete({ requestedLocation: true, locationGranted: false });
    } finally {
      setIsLocating(false);
    }
  }, [complete, isFinalSlide, isLocating]);

  const handleBack = React.useCallback(() => {
    setSlideIndex((current) => Math.max(0, current - 1));
  }, []);

  const handleTouchStart = React.useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch || isLocating) return;
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  }, [isLocating]);

  const handleTouchEnd = React.useCallback((event) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    const touch = event.changedTouches?.[0];
    if (!start || !touch || isLocating) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    const horizontalDistance = Math.abs(deltaX);
    const verticalDistance = Math.abs(deltaY);
    const isSwipe = horizontalDistance >= 56 && horizontalDistance > verticalDistance * 1.25;
    if (!isSwipe) return;

    if (deltaX < 0) {
      setSlideIndex((current) => Math.min(current + 1, onboardingSlides.length - 1));
    } else {
      setSlideIndex((current) => Math.max(0, current - 1));
    }
  }, [isLocating]);

  return (
    <Box
      onTouchCancel={() => {
        touchStartRef.current = null;
      }}
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchStart}
      sx={{
        height: "100dvh",
        minHeight: 0,
        overflow: "hidden",
        touchAction: "pan-y",
        color: "text.primary",
        background:
          "radial-gradient(circle at 20% 18%, rgba(86,195,114,0.18), transparent 32%), radial-gradient(circle at 90% 12%, rgba(234,176,22,0.14), transparent 28%), #131120",
        display: "flex",
        alignItems: "stretch",
        justifyContent: "center",
        px: { xs: 2, sm: 3 },
        pt: "calc(18px + env(safe-area-inset-top, 0px))",
        pb: "calc(18px + env(safe-area-inset-bottom, 0px))",
        "@media (max-height: 740px)": {
          pt: "calc(10px + env(safe-area-inset-top, 0px))",
          pb: "calc(10px + env(safe-area-inset-bottom, 0px))",
        },
        "@media (max-height: 620px)": {
          pt: "calc(8px + env(safe-area-inset-top, 0px))",
          pb: "calc(8px + env(safe-area-inset-bottom, 0px))",
        },
      }}
    >
      <Stack
        spacing={{ xs: 1.5, sm: 2.2, md: 3 }}
        sx={{
          width: "100%",
          maxWidth: 1040,
          height: "100%",
          minHeight: 0,
          "@media (max-height: 740px)": {
            gap: 1,
          },
          "@media (max-height: 620px)": {
            gap: 0.75,
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: 15, fontWeight: 900 }}>Inssa</Typography>
          <IconButton aria-label="Skip onboarding" onClick={handleSkip} sx={{ color: "text.primary" }}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "0.95fr 1.05fr" },
            gap: { xs: 1.65, md: 5 },
            alignItems: "center",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            "@media (max-height: 740px)": {
              gap: { xs: 0.85, md: 3 },
            },
            "@media (max-height: 620px)": {
              gap: { xs: 0.75, md: 2 },
            },
          }}
        >
          <Stack
            spacing={1.55}
            sx={{
              minHeight: 0,
              "@media (max-height: 740px)": {
                gap: 0.62,
              },
              "@media (max-height: 620px)": {
                gap: 0.65,
              },
            }}
          >
            <Stack direction="row" spacing={0.8} alignItems="center">
              {onboardingSlides.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    width: index === slideIndex ? 28 : 7,
                    height: 7,
                    borderRadius: 999,
                    backgroundColor:
                      index === slideIndex ? slide.accent : "rgba(255,255,255,0.28)",
                    transition: "width 180ms ease, background-color 180ms ease",
                  }}
                />
              ))}
            </Stack>

            <Typography
              sx={{
                color: slide.accent,
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {slide.eyebrow}
            </Typography>

            <Typography
              component="h1"
              sx={{
                maxWidth: 520,
                fontSize: {
                  xs: "clamp(28px, 8.5vw, 34px)",
                  sm: 44,
                  md: 54,
                },
                fontWeight: 950,
                lineHeight: 0.98,
                "@media (max-height: 740px)": {
                  fontSize: {
                    xs: "clamp(23px, 7vw, 28px)",
                    sm: 36,
                    md: 44,
                  },
                },
                "@media (max-height: 620px)": {
                  fontSize: { xs: "clamp(23px, 7vw, 28px)", sm: 34, md: 42 },
                },
              }}
            >
              {slide.title}
            </Typography>

            <Typography
              sx={{
                maxWidth: 520,
                color: "text.secondary",
                fontSize: { xs: 16, sm: 18 },
                lineHeight: 1.42,
                minHeight: { xs: 68, sm: 77 },
                "@media (max-height: 740px)": {
                  fontSize: { xs: 13.5, sm: 15 },
                  lineHeight: 1.32,
                  minHeight: { xs: 54, sm: 60 },
                },
                "@media (max-height: 620px)": {
                  fontSize: { xs: 12.5, sm: 14 },
                  lineHeight: 1.26,
                  minHeight: { xs: 47, sm: 53 },
                },
              }}
            >
              {slide.body}
            </Typography>
          </Stack>

          <Stack
            spacing={1.6}
            sx={{
              minHeight: 0,
              "@media (max-height: 740px)": {
                gap: 1,
              },
              "@media (max-height: 620px)": {
                gap: 0.75,
              },
            }}
          >
            <MapPreview slide={slide} />
            <ExampleTicker
              examples={slide.examples}
              accent={slide.accent}
              label={slide.exampleLabel}
            />
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={1.2}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flex: "0 0 auto",
            minHeight: 50,
            "@media (max-height: 740px)": {
              minHeight: 44,
            },
            "@media (max-height: 620px)": {
              minHeight: 40,
            },
          }}
        >
          <Button color="inherit" onClick={handleSkip} sx={{ px: 1.5 }}>
            Skip
          </Button>
          <Stack direction="row" spacing={1}>
            <IconButton
              aria-label="Previous onboarding slide"
              disabled={slideIndex === 0 || isLocating}
              onClick={handleBack}
              sx={{ color: "text.primary" }}
            >
              <ArrowBackRoundedIcon />
            </IconButton>
            <Button
              variant="contained"
              onClick={handlePrimary}
              disabled={isLocating}
              endIcon={isFinalSlide ? <MyLocationRoundedIcon /> : <ArrowForwardRoundedIcon />}
              sx={{
                minWidth: { xs: 174, sm: 210 },
                px: 2.4,
                py: 1.2,
                backgroundColor: slide.accent,
                color: "#fff",
                fontWeight: 900,
                "&:hover": {
                  backgroundColor: slide.accent,
                  filter: "brightness(0.94)",
                },
              }}
            >
              {isFinalSlide
                ? isLocating
                  ? "Finding you..."
                  : "Explore nearby"
                : "Next"}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
